// ============================================================
// TeamSettingsModal.jsx — مودال کامل تنظیمات گروه
// ============================================================
// طراحی: شیشه‌ای + هوشمند، هماهنگ با HTML طراحی‌شده
// نقش‌ها:
//   • معلم       → دسترسی کامل (ویرایش نام/لوگو، toggleها، حذف، مدیریت اعضا)
//   • نماینده    → مشاهده نام/لوگو + مدیریت اعضا (تغییر نقش، حذف)
//   • عضو        → مشاهده نام/لوگو + دیدن لیست اعضا (view only)
// ============================================================

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { getToken } from '../utils/secureStorage.js';
import { useExams } from '../components/Exams/ExamContext.jsx';
import './TeamSettingsModal.css';

const DEFAULT_AVATAR = '/avatars/avatar34.png';
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ============================================================
// 🐉 DRAGONS — کپی دقیق از MedalsPage.jsx (منبع واحد اپ)
// ============================================================
const DRAGONS = [
  { id: 200, name: 'تخم اژدها', emoji: '🥚', image: '', minScore: 0, c1: '#fbbf24', c2: '#d97706', glow: 'rgba(251,191,36,0.65)' },
  { id: 201, name: 'تازه کار', emoji: '🦎', image: '/medals/1.png', minScore: 10, c1: '#24fbd7', c2: '#06cbd9', glow: 'rgba(36,244,251,0.65)' },
  { id: 202, name: 'ماجراجو', emoji: '🦖', image: '/medals/2.png', minScore: 100, c1: '#84cc16', c2: '#65a30d', glow: 'rgba(132,204,22,0.65)' },
  { id: 203, name: 'حرفه ای', emoji: '🐲', image: '/medals/3.png', minScore: 500, c1: '#22d3ee', c2: '#0891b2', glow: 'rgba(34,211,238,0.65)' },
  { id: 204, name: 'قهرمان', emoji: '🐦‍🔥', image: '/medals/4.png', minScore: 1000, c1: '#f97316', c2: '#dc2626', glow: 'rgba(249,115,22,0.65)' },
  { id: 205, name: 'افسانه', emoji: '🐉', image: '/medals/5.png', minScore: 2000, c1: '#818cf8', c2: '#6366f1', glow: 'rgba(129,140,248,0.65)' },
  { id: 206, name: 'استاد', emoji: '🔱', image: '/medals/6.png', minScore: 3500, c1: '#bae6fd', c2: '#0284c7', glow: 'rgba(186,230,253,0.65)' },
  { id: 207, name: 'استاد بزرگ', emoji: '🔥', image: '/medals/7.png', minScore: 6000, c1: '#fde047', c2: '#eab308', glow: 'rgba(253,224,71,0.65)' },
  { id: 208, name: 'اسطوره', emoji: '👑', image: '/medals/8.png', minScore: 10000, c1: '#e879f9', c2: '#a855f7', glow: 'rgba(232,121,249,0.65)' },
];

const getLeagueIdxByScore = (xp) => {
  let i = 0;
  DRAGONS.forEach((d, k) => { if ((Number(xp) || 0) >= d.minScore) i = k; });
  return i;
};

const getDragonForXp = (xp) => DRAGONS[getLeagueIdxByScore(xp)];


const idsEqual = (a, b) => {
  if (a == null || b == null) return false;
  return String(a) === String(b);
};

const resolveAvatar = (raw) => {
  if (!raw) return DEFAULT_AVATAR;
  const s = String(raw).trim();
  if (!s) return DEFAULT_AVATAR;
  if (s.startsWith('/') || s.startsWith('http')) return s;
  return DEFAULT_AVATAR;
};

const fa = (n) => String(n ?? '').replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[d]);

// ============================================================
// کامپوننت
// ============================================================
export default function TeamSettingsModal({
  // داده
  team,
  members = [],
  presenceMap = {},
  currentUserId,
  currentUserRole,

  // تم
  isDarkMode = false,
  setIsDarkMode,

  // کال‌بک‌ها از useTeam
  updateTeamInfo,
  deleteTeam,
  changeMemberRole,
  removeMember,
  refreshTeam,

  // کال‌بک‌های والد
  onClose,
  onOpenAddMember,
  onViewProfile,
  navigate,

  // utilityها
  getUserName,
  getUserAvatar,
  getOnlineStatus,
  formatLastSeen,
  addToast,

  // لوگو
  logoOptions = [],
}) {
  // ─── دسترسی‌ها ───
  const isTeacher = currentUserRole === 'teacher';
  const { exams } = useExams();
  const currentMember = useMemo(
    () => members.find((m) => m && idsEqual(m.studentId, currentUserId)),
    [members, currentUserId]
  );
  const isAdmin = currentMember?.role === 'admin';
  const canEditTeam = isTeacher;
  const canManageMembers = isTeacher || isAdmin;
  const canDeleteTeam = isTeacher;

  // ─── stateها ───
  const [localName, setLocalName] = useState(team?.name || '');
  const [nameDirty, setNameDirty] = useState(false);
  const [savingName, setSavingName] = useState(false);
  const [savingJoinMode, setSavingJoinMode] = useState(false);
  const [savingRepToggle, setSavingRepToggle] = useState(false);
  const [savingField, setSavingField] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDeleteFinalConfirm, setShowDeleteFinalConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [rewardsMap, setRewardsMap] = useState({});
  const [menuForMember, setMenuForMember] = useState(null);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [showLeaveFinalConfirm, setShowLeaveFinalConfirm] = useState(false);
  const [leaving, setLeaving] = useState(false);

  // sync نام وقتی team عوض می‌شه
  useEffect(() => {
    setLocalName(team?.name || '');
    setNameDirty(false);
  }, [team?.name]);

  // ESC + قفل اسکرول
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        if (showDeleteFinalConfirm) { setShowDeleteFinalConfirm(false); return; }
        if (showDeleteConfirm) { setShowDeleteConfirm(false); return; }
        if (showLeaveFinalConfirm) { setShowLeaveFinalConfirm(false); return; }
        if (showLeaveConfirm) { setShowLeaveConfirm(false); return; }
        if (menuForMember) { setMenuForMember(null); return; }
        onClose?.();
      }
    };
    document.addEventListener('keydown', handleKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, showDeleteConfirm, showDeleteFinalConfirm, showLeaveConfirm, showLeaveFinalConfirm, menuForMember]);

  // ============================================================
  // ✅ XP هر عضو — کپی دقیق روش UserProfileModal (اثبات‌شده)
  //    منبع اصلی: GET /userRewards?userId=X → [0].xp
  //    seed اولیه: __allUsersCache[uid].xp (سریع، ممکن است کهنه)
  //    سپس: fetch تک‌تک برای هر عضوی که در seed نیست
  // ============================================================
  // ============================================================
  // ✅ XP هر عضو — قطعی با ۴ لایه (همه اثبات‌شده)
  //    منبع اصلی: /userRewards?userId=X  بدون header (مثل UserProfileModal)
  // ============================================================
  const memberIdsKey = useMemo(
    () =>
      (members || [])
        .map((m) => String(m?.studentId ?? ''))
        .filter(Boolean)
        .sort()
        .join('|'),
    [members]
  );

  useEffect(() => {
    let cancelled = false;

    // استخراج XP از object (هر نام فیلد — فقط مقادیر > 0)
    const extractXp = (obj) => {
      if (!obj || typeof obj !== 'object') return null;
      const fields = ['xp', 'XP', 'score', 'points', 'totalXp', 'totalXP'];
      for (const f of fields) {
        const raw = obj[f];
        if (raw === undefined || raw === null || raw === '') continue;
        const n = Number(raw);
        if (Number.isFinite(n) && n > 0) return n;
      }
      return null;
    };

    // محاسبه XP از exams (دقیقاً فرمول ParentDashboard)
    const computeXpFromExams = (uid) => {
      if (!uid || !Array.isArray(exams)) return 0;
      let total = 0;
      for (const exam of exams) {
        if (!exam) continue;
        const r = (exam.results || []).find(
          (x) => String(x.studentId) === String(uid)
        );
        if (!r || r.isFinalSubmit !== true || !r.submittedAt) continue;
        const max = (exam.questions || []).reduce(
          (s, q) => s + (Number(q.score) || 1),
          0
        );
        if (max <= 0) continue;
        const pct = (Number(r.score) / max) * 100;
        let xp = 0;
        if (exam.isChallenge || exam.isStepQuiz) {
          xp = Number(exam.rewardXP) || 0;
        } else if (pct >= 90) {
          xp = Number(exam.xpExcellent) || 0;
        } else if (pct >= 75) {
          xp = Number(exam.xpVeryGood) || 0;
        } else if (pct >= 50) {
          xp = Number(exam.xpGood) || 0;
        } else {
          xp = Number(exam.xpNeedsWork) || 0;
        }
        total += xp;
      }
      return total;
    };

    // fetch تک‌تک — دقیقاً مثل UserProfileModal (بدون header)
    const fetchOneXp = async (uid) => {
      try {
        const res = await fetch(
          `${API_BASE}/userRewards?userId=${encodeURIComponent(uid)}`
        );
        if (!res.ok) {
          console.warn(`[XP] fetch ${uid} → HTTP ${res.status}`);
          return null;
        }
        const data = await res.json();
        const arr = Array.isArray(data)
          ? data
          : (data && typeof data === 'object' ? [data] : []);
        const r = arr[0];
        if (!r) {
          console.warn(`[XP] fetch ${uid} → empty array`);
          return null;
        }
        const v = extractXp(r);
        console.log(`[XP] fetch ${uid} →`, r, '→', v);
        return v;
      } catch (e) {
        console.warn(`[XP] fetch ${uid} failed:`, e.message);
        return null;
      }
    };

    const loadRewards = async () => {
      try {
        const ids = Array.from(
          new Set(
            (members || [])
              .map((m) => m?.studentId)
              .filter((id) => id != null)
              .map(String)
          )
        );

        if (ids.length === 0) {
          if (!cancelled) setRewardsMap({});
          return;
        }

        const map = {};

        // ─── لایه ۱: fetch تک‌تک (منبع اصلی — مثل UserProfileModal) ───
        const CONCURRENCY = 4;
        for (let i = 0; i < ids.length; i += CONCURRENCY) {
          if (cancelled) return;
          const batch = ids.slice(i, i + CONCURRENCY);
          const results = await Promise.all(batch.map(fetchOneXp));
          batch.forEach((uid, idx) => {
            if (results[idx] !== null && results[idx] > 0) {
              map[uid] = results[idx];
            }
          });
        }

        // ─── لایه ۲: cache برای هر چیزی که هنوز > 0 نیست ───
        if (Array.isArray(window.__allUsersCache)) {
          for (const u of window.__allUsersCache) {
            if (!u || u.id == null) continue;
            const uid = String(u.id);
            if (!ids.includes(uid)) continue;
            if (map[uid] && map[uid] > 0) continue;
            const v = extractXp(u);
            if (v !== null && v > 0) map[uid] = v;
          }
        }

        // ─── لایه ۳: محاسبه از exams ───
        for (const uid of ids) {
          if (!map[uid] || map[uid] === 0) {
            const computed = computeXpFromExams(uid);
            if (computed > 0) map[uid] = computed;
          }
        }

        // ─── لایه ۴: 0 ───
        for (const uid of ids) {
          if (!(uid in map)) map[uid] = 0;
        }

        console.log('[XP] final map:', map);

        if (!cancelled) setRewardsMap(map);
      } catch (e) {
        console.warn('[TeamSettingsModal] rewards load error:', e);
      }
    };

    loadRewards();
    const interval = setInterval(loadRewards, 30000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [memberIdsKey, members, exams]);

  // ✅ بستن منوی اکشن با کلیک بیرون
  useEffect(() => {
    if (!menuForMember) return;
    const handleClick = (e) => {
      if (e.target.closest('.tsm-member-menu')) return;
      if (e.target.closest('.tsm-member-gear')) return;
      setMenuForMember(null);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('touchstart', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('touchstart', handleClick);
    };
  }, [menuForMember]);

  // ─── داده‌های مشتق ───
  const activeMembers = useMemo(
    () => members.filter((m) => m && (m.status === 'active' || !m.status)),
    [members]
  );

  const onlineCount = useMemo(
    () => activeMembers.filter((m) => getOnlineStatus?.(m.studentId) === 'online').length,
    [activeMembers, getOnlineStatus]
  );

  const rankedMembers = useMemo(() => {
    const arr = [...activeMembers];
    arr.sort((a, b) => {
      const xpA = rewardsMap[String(a.studentId)] || 0;
      const xpB = rewardsMap[String(b.studentId)] || 0;
      if (xpB !== xpA) return xpB - xpA;
      const nameA = (getUserName?.(a.studentId) || '').toLowerCase();
      const nameB = (getUserName?.(b.studentId) || '').toLowerCase();
      return nameA.localeCompare(nameB, 'fa');
    });
    return arr.map((m, i) => ({ member: m, rank: i + 1 }));
  }, [activeMembers, rewardsMap, getUserName]);

  const filteredMembers = useMemo(() => {
    if (!searchQuery.trim()) return rankedMembers;
    const q = searchQuery.trim().toLowerCase();
    return rankedMembers.filter(({ member }) => {
      const name = (getUserName?.(member.studentId) || '').toLowerCase();
      return name.includes(q);
    });
  }, [rankedMembers, searchQuery, getUserName]);

  // ─── هندلرها ───
  const handleSaveName = useCallback(async () => {
    if (!canEditTeam || savingName) return;
    const trimmed = localName.trim();
    if (!trimmed || trimmed === team?.name) {
      setNameDirty(false);
      return;
    }
    setSavingName(true);
    try {
      await updateTeamInfo({ name: trimmed });
      addToast?.('✅ نام گروه تغییر کرد', 'success');
      setNameDirty(false);
    } catch (e) {
      addToast?.(e?.message || 'خطا در ذخیره', 'error');
    } finally {
      setSavingName(false);
    }
  }, [canEditTeam, savingName, localName, team?.name, updateTeamInfo, addToast]);

  const handlePickLogo = useCallback(
    async (logoValue) => {
      if (!canEditTeam) return;
      if (logoValue === team?.image) return;
      setSavingField('image');
      try {
        await updateTeamInfo({ image: logoValue });
        addToast?.('✅ لوگوی گروه تغییر کرد', 'success');
      } catch (e) {
        addToast?.(e?.message || 'خطا', 'error');
      } finally {
        setSavingField(null);
      }
    },
    [canEditTeam, team?.image, updateTeamInfo, addToast]
  );

  const handleToggle = useCallback(
    async (field, currentValue) => {
      if (!canEditTeam || savingField === field) return;
      setSavingField(field);
      try {
        await updateTeamInfo({ [field]: !currentValue });
        addToast?.('✅ تنظیمات ذخیره شد', 'success');
      } catch (e) {
        addToast?.(e?.message || 'خطا', 'error');
      } finally {
        setSavingField(null);
      }
    },
    [canEditTeam, savingField, updateTeamInfo, addToast]
  );

  const handleDeleteClick = useCallback(() => {
    if (!canDeleteTeam) return;
    setShowDeleteConfirm(true);
  }, [canDeleteTeam]);

  const handleDeleteFirstConfirm = useCallback(() => {
    setShowDeleteConfirm(false);
    setShowDeleteFinalConfirm(true);
  }, []);

  const handleDeleteCancel = useCallback(() => {
    setShowDeleteConfirm(false);
    setShowDeleteFinalConfirm(false);
  }, []);

  const handleDeleteFinal = useCallback(async () => {
    if (!canDeleteTeam || deleting) return;
    setDeleting(true);
    try {
      await deleteTeam();
      addToast?.('گروه حذف شد', 'warning');
      setShowDeleteFinalConfirm(false);
      onClose?.();
      navigate?.('/home');
    } catch (e) {
      addToast?.(e?.message || 'خطا', 'error');
      setDeleting(false);
      setShowDeleteFinalConfirm(false);
    }
  }, [canDeleteTeam, deleting, deleteTeam, addToast, onClose, navigate]);

  // ✅ ترک گروه — فقط برای دانش‌آموزان (نه معلم)
  const handleLeaveClick = useCallback(() => {
    if (isTeacher) return;
    setShowLeaveConfirm(true);
  }, [isTeacher]);

  const handleLeaveFirstConfirm = useCallback(() => {
    setShowLeaveConfirm(false);
    setShowLeaveFinalConfirm(true);
  }, []);

  const handleLeaveCancel = useCallback(() => {
    setShowLeaveConfirm(false);
    setShowLeaveFinalConfirm(false);
  }, []);

  const handleLeaveFinal = useCallback(async () => {
    if (isTeacher || leaving) return;
    setLeaving(true);
    try {
      await removeMember(currentUserId);
      addToast?.('شما از گروه خارج شدید', 'info');
      setShowLeaveFinalConfirm(false);
      setLeaving(false);
      onClose?.();
      // رفرش تا TeamDashboard متوجه شود کاربر دیگر عضو نیست
      setTimeout(() => {
        refreshTeam?.();
      }, 150);
    } catch (e) {
      addToast?.(e?.message || 'خطا در خروج از گروه', 'error');
      setLeaving(false);
      setShowLeaveFinalConfirm(false);
    }
  }, [isTeacher, leaving, removeMember, currentUserId, addToast, onClose, refreshTeam]);

  const handleChangeRole = useCallback(
    async (member, newRole) => {
      if (!canManageMembers) return;
      if (idsEqual(member.studentId, currentUserId)) return;
      if (idsEqual(member.studentId, team?.teacherId)) return;
      if ((member.role || 'member') === newRole) return;
      try {
        await changeMemberRole(member.studentId, newRole);
        addToast?.('✅ نقش تغییر کرد', 'success');
      } catch (e) {
        addToast?.(e?.message || 'خطا', 'error');
      }
    },
    [canManageMembers, currentUserId, team?.teacherId, changeMemberRole, addToast]
  );

  const handleRemove = useCallback(
    async (member) => {
      if (!canManageMembers) return;
      if (idsEqual(member.studentId, currentUserId)) return;
      if (idsEqual(member.studentId, team?.teacherId)) return;
      const name = getUserName?.(member.studentId) || 'این عضو';
      if (!window.confirm(`آیا از حذف ${name} از گروه مطمئن هستید؟`)) return;
      try {
        await removeMember(member.studentId);
        addToast?.('✅ عضو حذف شد', 'success');
      } catch (e) {
        addToast?.(e?.message || 'خطا', 'error');
      }
    },
    [canManageMembers, currentUserId, team?.teacherId, getUserName, removeMember, addToast]
  );

  const handleIdentityAvatarClick = useCallback(() => {
    if (!canEditTeam) return;
    const el = document.getElementById('tsm-logo-picker');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [canEditTeam]);

  const openMemberMenu = useCallback((e, member) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const menuWidth = 210;
    const menuHeight = 240;
    let x = rect.right - menuWidth;
    let y = rect.bottom + 6;
    if (x < 8) x = 8;
    if (x + menuWidth > window.innerWidth - 8) x = window.innerWidth - menuWidth - 8;
    if (y + menuHeight > window.innerHeight - 8) y = rect.top - menuHeight - 6;
    if (y < 8) y = 8;
    setMenuForMember({ member, position: { x, y } });
  }, []);

  // ─── رندر ───
  const content = (
    <>
      <div
        className={`tsm-overlay ${isDarkMode ? 'tsm-dark' : 'tsm-light'}`}
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose?.();
        }}
        role="dialog"
        aria-modal="true"
        aria-label="تنظیمات گروه"
      >
        <div className="tsm-modal" onClick={(e) => e.stopPropagation()}>

        {/* ═══ HEADER ═══ */}
        <div className="tsm-header">
          <div className="tsm-header-title">
            <div className="tsm-header-icon">⚙️</div>
            <span>تنظیمات گروه</span>
          </div>
<div className="tsm-header-actions">
  {/*{setIsDarkMode && (
    <button
      type="button"
      className="tsm-icon-round"
      onClick={() => setIsDarkMode(!isDarkMode)}
      title="تغییر تم"
      aria-label="تغییر تم"
    >
      {isDarkMode ? '☀️' : '🌙'}
    </button>
  )}*/}
<button
  type="button"
  className="tsm-icon-round tsm-icon-round--text"
  onClick={() => onViewProfile?.(currentUserId)}
  title="مشاهده پروفایل"
  aria-label="مشاهده پروفایل"
>
  مشاهده پروفایل
</button>
  <button
    type="button"
    className="tsm-close-btn"
    onClick={onClose}
    title="بستن"
    aria-label="بستن"
  >
    ✕
  </button>
</div>
        </div>

        {/* ═══ BODY ═══ */}
        <div className="tsm-body">

          {/* ─── IDENTITY ─── */}
          <div className="tsm-identity">
            <div
              className={`tsm-identity-avatar ${canEditTeam ? '' : 'tsm-readonly'}`}
              onClick={handleIdentityAvatarClick}
              role={canEditTeam ? 'button' : undefined}
              tabIndex={canEditTeam ? 0 : undefined}
              title={canEditTeam ? 'انتخاب لوگو' : 'لوگوی گروه'}
              onKeyDown={(e) => {
                if (!canEditTeam) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleIdentityAvatarClick();
                }
              }}
            >
              {team?.image ? (
                <img
                  src={team.image}
                  alt="logo"
                  onError={(e) => {
                    if (!e.currentTarget.dataset.fb) {
                      e.currentTarget.dataset.fb = '1';
                      e.currentTarget.style.display = 'none';
                    }
                  }}
                />
              ) : (
                <span className="tsm-identity-emoji">📚</span>
              )}
            </div>

            <div className="tsm-identity-fields">
              <label className="tsm-identity-label">نام گروه</label>
              <div className="tsm-identity-input-row">
                <input
                  type="text"
                  className="tsm-identity-input"
                  value={localName}
                  onChange={(e) => {
                    setLocalName(e.target.value);
                    setNameDirty(e.target.value.trim() !== (team?.name || ''));
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveName();
                  }}
                  placeholder="نام گروه..."
                  disabled={!canEditTeam}
                  maxLength={60}
                />
                {canEditTeam && nameDirty && (
                  <button
                    type="button"
                    className="tsm-save-icon"
                    onClick={handleSaveName}
                    disabled={savingName}
                    title="ذخیره"
                    aria-label="ذخیره نام"
                  >
                    {savingName ? '⋯' : '💾'}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* ─── LOGO PICKER (teacher only) ─── */}
          {canEditTeam && logoOptions.length > 0 && (
            <div className="tsm-logo-picker" id="tsm-logo-picker">
              <div className="tsm-logo-picker-label">انتخاب لوگو</div>
              <div className="tsm-logo-scroll">
                {logoOptions.map((logo, idx) => {
                  const isActive = team?.image === logo;
                  const isSaving = savingField === 'image';
                  return (
                    <button
                      key={`${logo}-${idx}`}
                      type="button"
                      className={`tsm-logo-chip ${isActive ? 'active' : ''}`}
                      onClick={() => !isSaving && handlePickLogo(logo)}
                      title={`لوگو ${idx + 1}`}
                      aria-label={`انتخاب لوگوی ${idx + 1}`}
                      disabled={isSaving}
                    >
                      <img
                        src={logo}
                        alt=""
                        onError={(e) => {
                          if (!e.currentTarget.dataset.fb) {
                            e.currentTarget.dataset.fb = '1';
                            e.currentTarget.style.opacity = '0.3';
                          }
                        }}
                      />
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ─── SETTINGS (teacher only) ─── */}
          {canEditTeam && (
            <div className="tsm-settings-group">
              <div
                className="tsm-setting-row"
                onClick={() =>
                  handleToggle('allowStudentMessaging', !!team?.allowStudentMessaging)
                }
              >
                <div className="tsm-setting-icon">💬</div>
                <div className="tsm-setting-label">ارسال پیام دانش‌آموزان</div>
                <button
                  type="button"
                  className={`tsm-switch ${team?.allowStudentMessaging ? 'on' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggle('allowStudentMessaging', !!team?.allowStudentMessaging);
                  }}
                  disabled={savingField === 'allowStudentMessaging'}
                  aria-label="ارسال پیام دانش‌آموزان"
                />
              </div>

              <div
                className="tsm-setting-row"
                onClick={() => handleToggle('lockGroup', !!team?.lockGroup)}
              >
                <div className="tsm-setting-icon">🔒</div>
                <div className="tsm-setting-label">قفل گروه</div>
                <button
                  type="button"
                  className={`tsm-switch ${team?.lockGroup ? 'on' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggle('lockGroup', !!team?.lockGroup);
                  }}
                  disabled={savingField === 'lockGroup'}
                  aria-label="قفل گروه"
                />
              </div>

              <div
                className="tsm-setting-row"
                onClick={() => handleToggle('pinByMembers', !!team?.pinByMembers)}
              >
                <div className="tsm-setting-icon">📌</div>
                <div className="tsm-setting-label">سنجاق پیام توسط اعضا</div>
                <button
                  type="button"
                  className={`tsm-switch ${team?.pinByMembers ? 'on' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggle('pinByMembers', !!team?.pinByMembers);
                  }}
                  disabled={savingField === 'pinByMembers'}
                  aria-label="سنجاق پیام توسط اعضا"
                />
              </div>
            </div>
          )}

          {/* ─── JOIN MODE (teacher only) ─── */}
          {canEditTeam && (
            <>
              <div className="tsm-section-divider">
                <span>🚪 حالت عضویت گروه</span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '4px 4px 8px' }}>
                {[
                  { id: 'open', emoji: '🚪', label: 'باز', desc: 'هر کسی می‌تواند بپیوندد' },
                  { id: 'invite_only', emoji: '🎫', label: 'فقط با دعوت', desc: 'نیاز به تأیید مدیر' },
                  { id: 'closed', emoji: '🔒', label: 'بسته', desc: 'ورود جدید ممنوع' },
                ].map((m) => {
                  const isActive = (team?.joinMode || 'invite_only') === m.id;
                  return (
                    <button
                      key={m.id}
                      type="button"
                      onClick={async () => {
                        if (isActive || savingJoinMode) return;
                        setSavingJoinMode(true);
                        try {
                          await updateTeamInfo({ joinMode: m.id });
                          addToast?.(`✅ حالت عضویت: ${m.label}`, 'success');
                        } catch (e) {
                          addToast?.(e?.message || 'خطا', 'error');
                        } finally {
                          setSavingJoinMode(false);
                        }
                      }}
                      disabled={savingJoinMode}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: '12px 14px',
                        borderRadius: 12,
                        border: isActive ? '2px solid #6C5CE7' : '1.5px solid #e2e8f0',
                        background: isActive ? 'rgba(108,92,231,0.06)' : '#ffffff',
                        cursor: isActive ? 'default' : 'pointer',
                        textAlign: 'right',
                        fontFamily: 'inherit',
                        fontSize: '0.9rem',
                        transition: 'all 0.15s ease',
                      }}
                    >
                      <span style={{ fontSize: '1.4rem' }}>{m.emoji}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, color: '#1e293b' }}>{m.label}</div>
                        <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: 2 }}>
                          {m.desc}
                        </div>
                      </div>
                      {isActive && (
                        <span style={{ color: '#6C5CE7', fontWeight: 800, fontSize: '1.1rem' }}>✓</span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* نماینده: فقط وقتی invite_only */}
              {(team?.joinMode || 'invite_only') === 'invite_only' && (
                <div
                  className="tsm-setting-row"
                  onClick={async () => {
                    if (savingRepToggle) return;
                    setSavingRepToggle(true);
                    try {
                      await updateTeamInfo({
                        representativeCanApproveRequests: !team?.representativeCanApproveRequests,
                      });
                      addToast?.('✅ تنظیمات ذخیره شد', 'success');
                    } catch (e) {
                      addToast?.(e?.message || 'خطا', 'error');
                    } finally {
                      setSavingRepToggle(false);
                    }
                  }}
                  style={{ marginTop: 4 }}
                >
                  <div className="tsm-setting-icon">⭐</div>
                  <div className="tsm-setting-label">نماینده می‌تواند درخواست‌ها را تأیید/رد کند</div>
                  <button
                    type="button"
                    className={`tsm-switch ${team?.representativeCanApproveRequests ? 'on' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (savingRepToggle) return;
                      setSavingRepToggle(true);
                      updateTeamInfo({
                        representativeCanApproveRequests: !team?.representativeCanApproveRequests,
                      })
                        .then(() => addToast?.('✅ تنظیمات ذخیره شد', 'success'))
                        .catch((err) => addToast?.(err?.message || 'خطا', 'error'))
                        .finally(() => setSavingRepToggle(false));
                    }}
                    disabled={savingRepToggle}
                    aria-label="نماینده می‌تواند درخواست‌ها را تأیید/رد کند"
                  />
                </div>
              )}
            </>
          )}

          {/* ─── DANGER (teacher only) ─── */}
          {canDeleteTeam && (
            <div className="tsm-danger-row" onClick={handleDeleteClick} role="button" tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleDeleteClick();
                }
              }}
            >
              <div className="tsm-danger-icon">🗑️</div>
              <div className="tsm-danger-text">حذف گروه</div>
              <div className="tsm-danger-arrow">‹</div>
            </div>
          )}

          {/* ─── LEAVE GROUP (students only) ─── */}
          {!isTeacher && (
            <div className="tsm-danger-row" onClick={handleLeaveClick} role="button" tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleLeaveClick();
                }
              }}
            >
              <div className="tsm-danger-icon">🚪</div>
              <div className="tsm-danger-text">ترک گروه</div>
              <div className="tsm-danger-arrow">‹</div>
            </div>
          )}

          {/* ─── DIVIDER ─── */}
          <div className="tsm-divider">
            <span className="tsm-divider-label">
              👥 اعضای گروه
              <span className="tsm-divider-label-count">{fa(activeMembers.length)}</span>
            </span>
          </div>

          {/* ─── MEMBERS TOOLBAR ─── */}
          <div className="tsm-members-toolbar">
            <div className="tsm-search-wrap">
              <input
                type="text"
                className="tsm-search-input"
                placeholder="جستجوی دانش‌آموز..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="tsm-members-stats">
              <span className="tsm-dot-online" />
              <span>{fa(onlineCount)} آنلاین</span>
            </div>
          </div>

          {/* ─── MEMBERS LIST ─── */}
          <div className="tsm-member-list">
            {filteredMembers.length === 0 ? (
              <div className="tsm-empty">
                {searchQuery.trim()
                  ? 'دانش‌آموزی با این نام پیدا نشد'
                  : 'هنوز عضوی به گروه اضافه نشده'}
              </div>
            ) : (
              filteredMembers.map(({ member, rank }) => {
                const isOnline = getOnlineStatus?.(member.studentId) === 'online';
                const isTeacherRow = idsEqual(member.studentId, team?.teacherId);
                const isMe = idsEqual(member.studentId, currentUserId);
                const name = getUserName?.(member.studentId) || 'کاربر';
                const avatar = getUserAvatar?.(member.studentId) || DEFAULT_AVATAR;
const xp = rewardsMap[String(member.studentId)] || 0;
const dragon = getDragonForXp(xp);
                const canManageThis = canManageMembers && !isMe && !isTeacherRow;

                return (
                  <div
                    key={member.id || member.studentId}
                    className={`tsm-member ${isMe ? 'tsm-member--me' : ''}`}
                  >
                    <span className={`tsm-rank-badge ${rank <= 3 ? `tsm-rank-badge--${rank}` : ''}`}>
                      {fa(rank)}
                    </span>

                    <div
                      className="tsm-member-avatar"
                      onClick={() => onViewProfile?.(member.studentId)}
                      role="button"
                      tabIndex={0}
                      title="مشاهده پروفایل"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onViewProfile?.(member.studentId);
                        }
                      }}
                    >
                      <img
                        src={resolveAvatar(avatar)}
                        alt={name}
                        onError={(e) => {
                          if (!e.currentTarget.dataset.fb) {
                            e.currentTarget.dataset.fb = '1';
                            e.currentTarget.src = DEFAULT_AVATAR;
                          }
                        }}
                      />
<span
  className="tsm-dragon-badge"
  style={{
    background: ``,
    
  }}
  title={`لیگ: ${dragon.name} — ${fa(xp.toLocaleString())} XP`}
>
  {dragon.image ? (
    <img
      src={dragon.image}
      alt={dragon.name}
      onError={(e) => {
        if (!e.currentTarget.dataset.fb) {
          e.currentTarget.dataset.fb = '1';
          e.currentTarget.style.display = 'none';
          const sib = e.currentTarget.nextElementSibling;
          if (sib) sib.style.display = 'flex';
        }
      }}
    />
  ) : null}
  <span
    className="tsm-dragon-emoji"
    style={{ display: dragon.image ? 'none' : 'flex' }}
  >
    {dragon.emoji}
  </span>
</span>
                      <span className={`tsm-status-dot ${isOnline ? 'online' : ''}`} />
                    </div>

                    <div className="tsm-member-info">
                      <div className="tsm-member-name">
                        <span className="tsm-member-name-text">
                          {name}
                          {isMe && <span className="tsm-me-label">(شما)</span>}
                        </span>
                        {isTeacherRow && (
                          <span className="tsm-tag tsm-tag-teacher">👨‍🏫</span>
                        )}
                        {!isTeacherRow && member.role === 'admin' && (
                          <span className="tsm-tag tsm-tag-admin">⭐</span>
                        )}
                      </div>
                      <div className="tsm-member-meta">
                        <span className="tsm-member-xp">
                          <span className="tsm-member-xp-icon">⚡</span>
                          {fa(xp.toLocaleString())}
                        </span>
                        <span className="tsm-member-status-text">
                          {isOnline ? 'آنلاین' : formatLastSeen?.(member.studentId) || 'آفلاین'}
                        </span>
                      </div>
                    </div>

                    {canManageThis && (
                      <button
                        type="button"
                        className="tsm-member-gear"
                        onClick={(e) => openMemberMenu(e, member)}
                        title="مدیریت عضو"
                        aria-label="مدیریت عضو"
                      >
                        ⚙️
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* ─── ADD MEMBER ─── */}
          {canManageMembers && (
            <button
              type="button"
              className="tsm-add-member-btn"
              onClick={() => {
                onClose?.();
                onOpenAddMember?.();
              }}
            >
              ➕ افزودن دانش‌آموز
            </button>
          )}

        </div>
       </div>
      </div>

      {/* ═══ مودال تایید اول ═══ */}
      {showDeleteConfirm && (
        <div
          className={`tsm-confirm-overlay ${isDarkMode ? 'tsm-dark' : 'tsm-light'}`}
          onClick={handleDeleteCancel}
          role="dialog"
          aria-modal="true"
        >
          <div className="tsm-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="tsm-confirm-icon tsm-confirm-icon--warning">⚠️</div>
            <div className="tsm-confirm-title">حذف گروه</div>
            <p className="tsm-confirm-text">
              آیا از حذف گروه «{team?.name || 'گروه'}» مطمئن هستید؟
            </p>
            <div className="tsm-confirm-actions">
              <button
                type="button"
                className="tsm-confirm-btn"
                onClick={handleDeleteCancel}
              >
                انصراف
              </button>
              <button
                type="button"
                className="tsm-confirm-btn tsm-confirm-btn--danger"
                onClick={handleDeleteFirstConfirm}
              >
                بله، ادامه
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ مودال تایید نهایی ═══ */}
            {/* ═══ مودال تایید نهایی ═══ */}
      {showDeleteFinalConfirm && (
        <div
          className={`tsm-confirm-overlay ${isDarkMode ? 'tsm-dark' : 'tsm-light'}`}
          onClick={deleting ? undefined : handleDeleteCancel}
          role="dialog"
          aria-modal="true"
        >
          <div className="tsm-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="tsm-confirm-icon">🗑️</div>
            <div className="tsm-confirm-title">تایید نهایی</div>
            <p className="tsm-confirm-text">
              این عمل <strong>قابل بازگشت نیست</strong>! تمام پیام‌ها، اعضا و اطلاعات گروه برای همیشه حذف خواهند شد.
            </p>
            <div className="tsm-confirm-actions">
              <button
                type="button"
                className="tsm-confirm-btn"
                onClick={handleDeleteCancel}
                disabled={deleting}
              >
                انصراف
              </button>
              <button
                type="button"
                className="tsm-confirm-btn tsm-confirm-btn--danger"
                onClick={handleDeleteFinal}
                disabled={deleting}
              >
                {deleting ? '⋯ در حال حذف' : 'بله، حذف کن'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ مودال تایید ترک (مرحله ۱) ═══ */}
      {showLeaveConfirm && (
        <div
          className={`tsm-confirm-overlay ${isDarkMode ? 'tsm-dark' : 'tsm-light'}`}
          onClick={handleLeaveCancel}
          role="dialog"
          aria-modal="true"
        >
          <div className="tsm-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="tsm-confirm-icon tsm-confirm-icon--warning">⚠️</div>
            <div className="tsm-confirm-title">ترک گروه</div>
            <p className="tsm-confirm-text">
              آیا از ترک گروه «{team?.name || 'گروه'}» مطمئن هستید؟
            </p>
            <div className="tsm-confirm-actions">
              <button
                type="button"
                className="tsm-confirm-btn"
                onClick={handleLeaveCancel}
              >
                انصراف
              </button>
              <button
                type="button"
                className="tsm-confirm-btn tsm-confirm-btn--danger"
                onClick={handleLeaveFirstConfirm}
              >
                بله، ادامه
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ مودال تایید نهایی ترک (مرحله ۲) ═══ */}
      {showLeaveFinalConfirm && (
        <div
          className={`tsm-confirm-overlay ${isDarkMode ? 'tsm-dark' : 'tsm-light'}`}
          onClick={leaving ? undefined : handleLeaveCancel}
          role="dialog"
          aria-modal="true"
        >
          <div className="tsm-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="tsm-confirm-icon">🚪</div>
            <div className="tsm-confirm-title">تایید نهایی</div>
            <p className="tsm-confirm-text">
              این عمل <strong>قابل بازگشت نیست</strong>! برای عضویت مجدد، باید معلم دعوت‌نامه بفرستد.
            </p>
            <div className="tsm-confirm-actions">
              <button
                type="button"
                className="tsm-confirm-btn"
                onClick={handleLeaveCancel}
                disabled={leaving}
              >
                انصراف
              </button>
              <button
                type="button"
                className="tsm-confirm-btn tsm-confirm-btn--danger"
                onClick={handleLeaveFinal}
                disabled={leaving}
              >
                {leaving ? '⋯ در حال خروج' : 'بله، خارج شو'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ منوی اکشن اعضا (فقط برای معلم/نماینده) ═══ */}
      {menuForMember && (
        <div
          className={`tsm-member-menu ${isDarkMode ? 'tsm-dark' : 'tsm-light'}`}
          style={{
            position: 'fixed',
            top: menuForMember.position.y,
            left: menuForMember.position.x,
            zIndex: 9260,
          }}
          onClick={(e) => e.stopPropagation()}
          role="menu"
        >
          <div className="tsm-member-menu-header">
            <span className="tsm-member-menu-name">
              {getUserName?.(menuForMember.member.studentId) || 'کاربر'}
            </span>
            <button
              type="button"
              className="tsm-member-menu-close"
              onClick={() => setMenuForMember(null)}
              aria-label="بستن"
            >
              ✕
            </button>
          </div>

          <div className="tsm-member-menu-section-label">نقش</div>
          <div className="tsm-member-menu-roles">
            <button
              type="button"
              className={`tsm-member-menu-role ${(menuForMember.member.role || 'member') === 'member' ? 'active' : ''}`}
              onClick={() => {
                handleChangeRole(menuForMember.member, 'member');
                setMenuForMember(null);
              }}
            >
              عضو
            </button>
            <button
              type="button"
              className={`tsm-member-menu-role ${menuForMember.member.role === 'admin' ? 'active' : ''}`}
              onClick={() => {
                handleChangeRole(menuForMember.member, 'admin');
                setMenuForMember(null);
              }}
            >
              نماینده
            </button>
          </div>

          <div className="tsm-member-menu-divider" />

          <button
            type="button"
            className="tsm-member-menu-item"
            onClick={() => {
              onViewProfile?.(menuForMember.member.studentId);
              setMenuForMember(null);
            }}
          >
            <span className="tsm-member-menu-icon">👤</span>
            <span>مشاهده پروفایل</span>
          </button>

          <button
            type="button"
            className="tsm-member-menu-item tsm-member-menu-item--danger"
            onClick={() => {
              handleRemove(menuForMember.member);
              setMenuForMember(null);
            }}
          >
            <span className="tsm-member-menu-icon">🗑️</span>
            <span>حذف از گروه</span>
          </button>
        </div>
      )}
    </>
  );

  if (typeof document === 'undefined') return content;
  return createPortal(content, document.body);
}